import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLayoutContext } from '../../../provider/layout/LayoutContext';

const ZendeskWidget = () => {
  const { zendeskUrl } = useSelector((state) => state.app.context?.project?.data);
  const { alwaysHideLiveChat, showLiveChat, globalNodeLoading } = useLayoutContext();
  const [zEObjectExists, setZObjectExists] = useState(false);
  const [userInactive, setUserInactive] = useState(false);
  const [timeIsUp, setTimeIsUp] = useState(false);

  const loadScriptZdassets = useCallback(async () => {
    if (!window?.zE) {
      const [head] = document.getElementsByTagName('head');
      const newScript = await document.createElement('script');
      await Object.assign(newScript, {
        id: 'ze-snippet',
        defer: true,
        async: true,
        src: zendeskUrl,
      });

      await head.appendChild(newScript);

      return new Promise((resolve, reject) => {
        newScript.onload = () => {
          setZObjectExists(true);
          window?.zE && window?.zE('messenger:set', 'zIndex', -1);
          window?.zE && window?.zE('messenger', 'hide');
          resolve();
        };
        newScript.onerror = (error) => {
          reject(error);
        };
      });
    }
  }, [zendeskUrl]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.requestIdleCallback =
        window.requestIdleCallback ||
        function (cb) {
          const start = Date.now();
          return setTimeout(() => {
            cb({
              didTimeout: false,
              timeRemaining: () => {
                return Math.max(0, 50 - (Date.now() - start));
              },
            });
          }, 1);
        };
      window.requestIdleCallback(loadScriptZdassets);
    }
  }, [loadScriptZdassets]);


  useEffect(() => {
    if (zEObjectExists && (showLiveChat || !alwaysHideLiveChat) && !timeIsUp) {
      const noInteractionDelay = 60000;
      const interactionDelay = 120000;

      const handleUserInteraction = () => {
        clearTimeout(noInteractionTimeoutId);
        setTimeout(() => {
          window?.zE && window?.zE('messenger', 'show');
          setTimeIsUp(true);
        }, interactionDelay);
        setUserInactive(true);
      };

      const noInteractionTimeoutId = setTimeout(() => {
        if (!userInactive) {
          window?.zE && window?.zE('messenger', 'show');
          setTimeIsUp(true);
        }
      }, noInteractionDelay);

      const bodyWrapper = document.getElementById('bodyWrapper');
      window.addEventListener('click', handleUserInteraction);
      window.addEventListener('keydown', handleUserInteraction);
      document.addEventListener('scroll', handleUserInteraction);
      bodyWrapper.addEventListener('touchmove', handleUserInteraction);

      return () => {
        window.removeEventListener('click', handleUserInteraction);
        window.removeEventListener('keydown', handleUserInteraction);
        document.removeEventListener('scroll', handleUserInteraction);
        bodyWrapper.removeEventListener('touchmove', handleUserInteraction);
      };
    }
  }, [zEObjectExists, alwaysHideLiveChat, userInactive]);

  useEffect(() => {
    if (window?.zE) {
      if (!showLiveChat || alwaysHideLiveChat || globalNodeLoading) {
        window?.zE && window?.zE('messenger:set', 'zIndex', -1);
        window?.zE && window?.zE('messenger', 'hide');
        return;
      }
      if (timeIsUp) {
        window?.zE && window?.zE('messenger:set', 'zIndex', 99999);
        window?.zE && window?.zE('messenger', 'show');
      }
    }
  }, [showLiveChat, alwaysHideLiveChat, globalNodeLoading, userInactive, timeIsUp]);

  return (
    <Helmet>
      <link rel="preconnect" href="https://static.zdassets.com" />
      {zendeskUrl && <link rel="preload" as="script" href={zendeskUrl} />}
    </Helmet>
  );
};

export default ZendeskWidget;
